import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { FC, RefObject } from 'react';
import { createPortal } from 'react-dom';
import { Button, Input } from 'antd';

import FloatingActionCheckIcon from 'assets/svg/FloatingActionCheckIcon';
import FloatingActionCloseIcon from 'assets/svg/FloatingActionCloseIcon';
import { colorConstants } from 'config/colorConstants';

import {
    BaseActionButtonClassName,
    BaseActionButtonsContainerStyle,
    BaseCancelActionButtonStyle,
    BaseConfirmActionButtonStyle,
    BaseControlTypography,
    BaseInputBaseClassName,
    buildInputStyle,
    getResolvedInputMode,
    renderWithOptionalTooltip,
    resolveTooltipText,
    sanitizeInputValue,
    type GenericInputBoxControlProps,
    type GenericPrefixedInputBoxControlProps,
} from './formControl';

type UseInputDraftStateArgs = {
    value: string;
    showSave: boolean;
    disabled?: boolean;
    onChange: (value: string) => void;
    onSave?: (value: string) => void;
    containerRef: RefObject<HTMLDivElement>;
    sanitizeValue?: (rawValue: string) => string;
};

const useInputDraftState = ({
    value,
    showSave,
    disabled,
    onChange,
    onSave,
    containerRef,
    sanitizeValue,
}: UseInputDraftStateArgs) => {
    const [draftValue, setDraftValue] = useState(value);
    const [isInputActive, setIsInputActive] = useState(false);

    useEffect(() => {
        if (showSave) setDraftValue(value);
    }, [showSave, value]);

    const renderedValue = showSave ? draftValue : value;
    const hasPendingChanges = showSave && draftValue !== value;

    const handleValueChange = (nextValue: string) => {
        const resolvedValue = sanitizeValue ? sanitizeValue(nextValue) : nextValue;
        if (showSave) {
            setDraftValue(resolvedValue);
            return;
        }
        onChange(resolvedValue);
    };

    const handleSave = () => {
        const nextValue = draftValue;
        if (onSave) {
            onSave(nextValue);
            return;
        }
        onChange(nextValue);
    };

    const handleCancel = () => {
        setDraftValue(value);
        setIsInputActive(false);
    };

    const handleConfirm = () => {
        if (!hasPendingChanges || disabled) return;
        handleSave();
        setIsInputActive(false);
    };

    const handleBlur = () => {
        window.setTimeout(() => {
            if (!containerRef.current?.contains(document.activeElement)) {
                setIsInputActive(false);
            }
        }, 0);
    };

    return {
        draftValue,
        renderedValue,
        hasPendingChanges,
        showFloatingActions: showSave && isInputActive,
        setIsInputActive,
        handleValueChange,
        handleBlur,
        handleCancel,
        handleConfirm,
    };
};

type FloatingInputActionsProps = {
    visible: boolean;
    disabled?: boolean;
    canConfirm: boolean;
    cancelAriaLabel: string;
    confirmAriaLabel: string;
    onCancel: () => void;
    onConfirm: () => void;
    containerRef: RefObject<HTMLDivElement>;
};

const FloatingInputActions = ({
    visible,
    disabled,
    canConfirm,
    cancelAriaLabel,
    confirmAriaLabel,
    onCancel,
    onConfirm,
    containerRef,
}: FloatingInputActionsProps) => {
    const [position, setPosition] = useState({ top: 0, left: 0 });

    useLayoutEffect(() => {
        if (!visible) return undefined;

        const updatePosition = () => {
            const container = containerRef.current;
            if (!container) return;
            const rect = container.getBoundingClientRect();
            setPosition({ top: rect.bottom + 6, left: rect.right });
        };

        updatePosition();
        window.addEventListener('scroll', updatePosition, true);
        window.addEventListener('resize', updatePosition);
        return () => {
            window.removeEventListener('scroll', updatePosition, true);
            window.removeEventListener('resize', updatePosition);
        };
    }, [containerRef, visible]);

    if (!visible) return null;

    return createPortal(
        <div
            className="flex items-center gap-1"
            style={{
                ...BaseActionButtonsContainerStyle,
                position: 'fixed',
                top: position.top,
                left: position.left,
                transform: 'translateX(-100%)',
                zIndex: 1100,
            }}
        >
            <Button
                size="small"
                aria-label={cancelAriaLabel}
                className={BaseActionButtonClassName}
                style={BaseCancelActionButtonStyle}
                onMouseDown={(event) => event.preventDefault()}
                onClick={onCancel}
            >
                <FloatingActionCloseIcon />
            </Button>
            <Button
                size="small"
                aria-label={confirmAriaLabel}
                className={BaseActionButtonClassName}
                style={BaseConfirmActionButtonStyle}
                onMouseDown={(event) => event.preventDefault()}
                onMouseEnter={(event) => {
                    if (disabled || !canConfirm) return;
                    event.currentTarget.style.borderColor = colorConstants.purpleFontSelected;
                }}
                onMouseLeave={(event) => {
                    event.currentTarget.style.borderColor = colorConstants.toolbarBorderGrey;
                }}
                onClick={onConfirm}
                disabled={disabled || !canConfirm}
            >
                <FloatingActionCheckIcon />
            </Button>
        </div>,
        document.body,
    );
};

export const GenericInputBoxControl: FC<GenericInputBoxControlProps> = ({
    value,
    onChange,
    showSave = false,
    onSave,
    saveButtonText = 'Save changes',
    allowedValueType = 'any',
    allowDecimal = false,
    decimalPlaces,
    disabled,
    className,
    containerClassName,
    showTooltip = false,
    tooltipText,
    staticTooltipText,
    ...inputProps
}) => {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const {
        style: inputStyle,
        onFocus,
        onBlur,
        onClick,
        inputMode: providedInputMode,
        type: _inputType,
        ...restInputProps
    } = inputProps;

    const sanitizeValue = useCallback(
        (rawValue: string) => sanitizeInputValue(rawValue, allowedValueType, allowDecimal, decimalPlaces),
        [allowDecimal, allowedValueType, decimalPlaces],
    );

    const resolvedInputMode = getResolvedInputMode(providedInputMode, allowedValueType, allowDecimal);

    const {
        draftValue,
        renderedValue,
        hasPendingChanges,
        showFloatingActions,
        setIsInputActive,
        handleValueChange,
        handleBlur,
        handleCancel,
        handleConfirm,
    } = useInputDraftState({
        value,
        showSave,
        disabled,
        onChange,
        onSave,
        containerRef,
        sanitizeValue,
    });

    const resolvedTooltipText = resolveTooltipText(staticTooltipText, tooltipText, showSave ? draftValue : value);

    const inputControl = (
        <Input
            {...restInputProps}
            type="text"
            disabled={disabled}
            value={renderedValue}
            inputMode={resolvedInputMode}
            onChange={(event) => handleValueChange(event.target.value)}
            onFocus={(event) => {
                event.currentTarget.style.borderColor = colorConstants.purpleFontSelected;
                onFocus?.(event);
                setIsInputActive(true);
            }}
            onClick={(event) => {
                onClick?.(event);
                setIsInputActive(true);
            }}
            onBlur={(event) => {
                event.currentTarget.style.borderColor = colorConstants.inputBorderMuted;
                onBlur?.(event);
                handleBlur();
            }}
            className={`${BaseInputBaseClassName} flex-1 !px-3 !text-[14px] !leading-[22px] ${className ?? ''}`.trim()}
            style={buildInputStyle(inputStyle)}
        />
    );

    return (
        <div ref={containerRef} className={`relative w-full ${containerClassName ?? ''}`.trim()}>
            {renderWithOptionalTooltip(inputControl, showTooltip, resolvedTooltipText)}
            <FloatingInputActions
                visible={showFloatingActions}
                disabled={disabled}
                canConfirm={hasPendingChanges}
                cancelAriaLabel="Discard changes"
                confirmAriaLabel={saveButtonText}
                onCancel={handleCancel}
                onConfirm={handleConfirm}
                containerRef={containerRef}
            />
        </div>
    );
};

export const GenericPrefixedInputBoxControl: FC<GenericPrefixedInputBoxControlProps> = ({
    prefixText,
    value,
    onChange,
    showSave = false,
    onSave,
    saveButtonText = 'Save changes',
    allowedValueType = 'any',
    allowDecimal = false,
    decimalPlaces,
    disabled,
    className,
    containerClassName,
    showTooltip = false,
    tooltipText,
    staticTooltipText,
    ...inputProps
}) => {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const {
        style: inputStyle,
        onFocus,
        onBlur,
        onClick,
        inputMode: providedInputMode,
        suffix: suffixContent,
        type: _inputType,
        ...restInputProps
    } = inputProps;

    const sanitizeValue = useCallback(
        (rawValue: string) => sanitizeInputValue(rawValue, allowedValueType, allowDecimal, decimalPlaces),
        [allowDecimal, allowedValueType, decimalPlaces],
    );

    const resolvedInputMode = getResolvedInputMode(providedInputMode, allowedValueType, allowDecimal);

    const {
        draftValue,
        renderedValue,
        hasPendingChanges,
        showFloatingActions,
        setIsInputActive,
        handleValueChange,
        handleBlur,
        handleCancel,
        handleConfirm,
    } = useInputDraftState({
        value,
        showSave,
        disabled,
        onChange,
        onSave,
        containerRef,
        sanitizeValue,
    });

    const prefixLabel = prefixText ?? '';
    const resolvedTooltipText = resolveTooltipText(
        staticTooltipText,
        tooltipText,
        `${prefixLabel}${showSave ? draftValue : value}`,
    );
    const resolvedPrefix = prefixLabel ? (
        <span style={{ ...BaseControlTypography, color: 'inherit' }}>{prefixLabel}</span>
    ) : undefined;
    const resolvedSuffix =
        typeof suffixContent === 'string' || typeof suffixContent === 'number' ? (
            <span style={{ ...BaseControlTypography, color: 'inherit' }}>{suffixContent}</span>
        ) : (
            suffixContent
        );

    const inputControl = (
        <Input
            {...restInputProps}
            type="text"
            prefix={resolvedPrefix}
            suffix={resolvedSuffix}
            disabled={disabled}
            value={renderedValue}
            inputMode={resolvedInputMode}
            onChange={(event) => handleValueChange(event.target.value)}
            onFocus={(event) => {
                event.currentTarget.style.borderColor = colorConstants.purpleFontSelected;
                onFocus?.(event);
                setIsInputActive(true);
            }}
            onClick={(event) => {
                onClick?.(event);
                setIsInputActive(true);
            }}
            onBlur={(event) => {
                event.currentTarget.style.borderColor = String(inputStyle?.borderColor ?? colorConstants.inputBorderMuted);
                onBlur?.(event);
                handleBlur();
            }}
            className={`${BaseInputBaseClassName} flex-1 !px-3 [&_.ant-input-prefix]:!me-2 [&_.ant-input]:!p-0 [&_.ant-input]:!text-[14px] [&_.ant-input]:!leading-[22px] ${className ?? ''}`.trim()}
            style={buildInputStyle(inputStyle)}
        />
    );

    return (
        <div ref={containerRef} className={`relative w-full ${containerClassName ?? ''}`.trim()}>
            {renderWithOptionalTooltip(inputControl, showTooltip, resolvedTooltipText)}
            <FloatingInputActions
                visible={showFloatingActions}
                disabled={disabled}
                canConfirm={hasPendingChanges}
                cancelAriaLabel="Discard changes"
                confirmAriaLabel={saveButtonText}
                onCancel={handleCancel}
                onConfirm={handleConfirm}
                containerRef={containerRef}
            />
        </div>
    );
};

export default GenericInputBoxControl;
